import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search } from 'lucide-react';
import LoadingSpinner from '../../../../src/components/LoadingSpinner';
import { supabase } from '../../../../src/lib/supabase';
import { Profile } from '../../../../types';
import { rotuloFuncaoComercial } from '../../../../src/lib/departamentos';

interface TransferUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (user: Profile) => void;
    currentResponsibleId?: string;
    /** Restringe a lista a papéis específicos (ex: só gerentes, para transferir uma aprovação de desconto). Sem filtro = todos os perfis ativos. */
    roleFilter?: string[];
    /** Título do cabeçalho do modal. Default: "Transferir Negociação". */
    title?: string;
    /** Filtro extra de destino (ex.: quem supervisiona o Comercial só passa para Closers, BDRs e ele mesmo). */
    filtrarDestino?: (p: Profile) => boolean;
}

const TransferUserModal: React.FC<TransferUserModalProps> = ({ isOpen, onClose, onSelect, currentResponsibleId, roleFilter, title, filtrarDestino }) => {
    const [users, setUsers] = useState<Profile[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            setSearchTerm('');
        }
    }, [isOpen]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('profiles')
                .select('*')
                .eq('status', 'active')
                .neq('role', 'consumidor');

            if (roleFilter && roleFilter.length > 0) {
                query = query.in('role', roleFilter);
            }

            const { data, error } = await query.order('full_name');

            if (error) throw error;
            setUsers(data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
        && (!filtrarDestino || filtrarDestino(user))
    );

    if (!isOpen) return null;

    // z acima dos pop-ups automáticos (AprovacaoDescontoPopup e TermoAssinadoPopup usam z-[100000]):
    // este modal é sempre aberto POR CIMA de outra tela, e em z-[110] ele abria atrás do backdrop
    // deles — o botão "Transferir" parecia não funcionar (nada aparecia ao clicar).
    return createPortal(
        <div className="fixed inset-0 z-[100010] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden transform scale-100 transition-all">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50 dark:bg-slate-800/50">
                    <h3 className="font-bold text-gray-800 dark:text-slate-100">
                        {title || 'Transferir Negociação'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500 dark:text-slate-400"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-50 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Buscar responsável..."
                            className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl text-sm focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                            autoFocus
                        />
                    </div>
                </div>

                {/* List */}
                <div className="max-h-80 overflow-y-auto custom-scrollbar p-2">
                    {loading ? (
                        <div className="p-4"><LoadingSpinner label="Carregando..." size="sm" /></div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-gray-400 dark:text-slate-500 text-sm">Nenhum usuário encontrado.</div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            {filteredUsers.map(user => {
                                const isCurrent = user.id === currentResponsibleId;
                                return (
                                    <button
                                        key={user.id}
                                        onClick={() => !isCurrent && onSelect(user)}
                                        disabled={isCurrent}
                                        className={`flex items-center gap-3 p-3 rounded-xl transition-all text-left group
                                            ${isCurrent ? 'bg-gray-50 dark:bg-slate-800/50 opacity-60 cursor-default' : 'hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer'}
                                        `}
                                    >
                                        {user.avatar_url ? (
                                            <img src={user.avatar_url} alt={user.full_name} className="w-8 h-8 rounded-full border border-gray-100 dark:border-slate-700 object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-gray-500 dark:text-slate-400">
                                                {user.full_name.charAt(0)}
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <p className={`text-sm font-semibold ${isCurrent ? 'text-gray-500 dark:text-slate-400' : 'text-gray-800 dark:text-slate-100 group-hover:text-primary'}`}>
                                                {user.full_name} {isCurrent && '(Atual)'}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 capitalize">{rotuloFuncaoComercial(user) || user.role}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TransferUserModal;
